// src/pages/invoicing/view-tax.js
import React, { useEffect, useState } from "react";
import Header from "../../components/Header";
import { Link } from "react-router-dom";
import API_BASE_URL from "../../config";
import DataTable from "react-data-table-component";

const ViewTax = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Default month range
  const today = new Date();
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    .toISOString()
    .split("T")[0];

  const [fromDate, setFromDate] = useState(firstDay);
  const [toDate, setToDate] = useState(lastDay);
  const [fromDateType, setFromDateType] = useState("text");
  const [toDateType, setToDateType] = useState("text");

  // Brand id -> name map
  const [brandsMap, setBrandsMap] = useState({});

  useEffect(() => {
    document.title = "View Tax Invoices | Webyappy";
  }, []);

  useEffect(() => {
    fetch(`${API_BASE_URL}/invoicing/get_brands`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") {
          const m = {};
          (d.brands || []).forEach((b) => (m[b.id] = b.brand_name));
          setBrandsMap(m);
        }
      })
      .catch(() => {});
  }, []);

  // Load all tax invoices in date range
  useEffect(() => {
    setLoading(true);
    fetch(
      `${API_BASE_URL}/invoicing/get_all_tax_invoices?start_date=${fromDate}&end_date=${toDate}`,
      { credentials: "include" }
    )
      .then((r) => r.json())
      .then((d) => {
        if (d.status === "success") setRows(d.tax_invoices || []);
      })
      .finally(() => setLoading(false));
  }, [fromDate, toDate]);

  const typeOf = (r) =>
    r.reference_pi_no && String(r.reference_pi_no).trim() !== ""
      ? "Against PI"
      : "Standalone";

  const columns = [
    { name: "TI No", selector: (r) => r.ti_no, sortable: true },
    { name: "Company", selector: (r) => r.to_company || "-", sortable: true },
    {
      name: "Brand",
      selector: (row) => brandsMap[row.brand_id] || "-",
      sortable: true,
    },
    {
      name: "Type",
      selector: (r) => typeOf(r),
      sortable: true,
      grow: 0.6,
      cell: (r) => (
        <span
          className={`badge ${
            typeOf(r) === "Against PI" ? "badge-primary" : "badge-info"
          } font-weight-bold`}
        >
          {typeOf(r)}
        </span>
      ),
    },
    {
      name: "Amount",
      selector: (row) =>
        `₹${Math.round(parseFloat(row.grand_total || 0)).toLocaleString("en-IN")}`,
      sortable: true,
      right: true,
    },
    {
      name: "Generated On",
      selector: (r) =>
        new Date(r.created_on).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }),
      sortable: true,
    },
    {
      name: "Status",
      selector: (r) => r.status || "Due",
      sortable: true,
      cell: (r) => (
        <span
          className={`badge ${
            r.status === "Paid" ? "badge-success" : "badge-danger"
          }`}
        >
          {r.status || "Due"}
        </span>
      ),
      grow: 0.6,
    },
    {
    name: "Generated By",
    selector: (r) => r.created_by_name || r.created_by || "-",
    sortable: true,
    },
    {
      name: "Action",
      cell: (r) => (
        <>
          {/* View TI */}
          <a
            href={`/pages/invoicing/ti-view/${r.id}`}
            target="_blank"
            rel="noopener noreferrer"
            title="View Tax Invoice"
            className="mr-3"
            style={{ marginRight: "10px" }}
          >
            <i className="far fa-eye text-danger icon-lg"></i>
          </a>

          {/* Edit TI */}
          <Link
            to={`/pages/invoicing/edit-ti-items/${r.id}`}
            title="Edit Tax Invoice"
          >
            <i className="fas fa-pencil-alt icon-lg text-danger"></i>
          </Link>
        </>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const filteredRows = rows.filter((r) => {
  const ti = (r.ti_no || "").toLowerCase();
  const comp = (r.to_company || "").toLowerCase();
  const brand = (brandsMap[r.brand_id] || "").toLowerCase();
  const type = typeOf(r).toLowerCase();
  const gen = (r.created_by_name || r.created_by || "").toLowerCase();
  const q = searchTerm.toLowerCase();
  return ti.includes(q) || comp.includes(q) || brand.includes(q) || type.includes(q) || gen.includes(q);
});

// Format date as DD-MMM-YYYY (full year)
const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const day = d.toLocaleString("en-GB", { day: "2-digit" });
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const yr  = d.getFullYear();
  return `${day}-${mon}-${yr}`;
};

// Format for file name: DD-MMM-YYYY
const formatForFile = (dateStr) => {
  const d = new Date(dateStr);
  const day = d.toLocaleString("en-GB", { day: "2-digit" });
  const mon = d.toLocaleString("en-GB", { month: "short" });
  const yr  = d.getFullYear();
  return `${day}-${mon}-${yr}`;
};




// Escape CSV values
const csvCell = (v) => {
  const s = (v ?? "").toString();
  if (s.includes('"') || s.includes(",") || s.includes("\n")) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
};

// Export to CSV
const exportToCSV = () => {
  const rowsToExport = filteredRows || [];
  const headers = [
    "Invoice Number","Date","Client Name","GST No.","Amt.","CGST","SGST","IGST","Total GST","Total Amount"
  ];

  const lines = [];
  lines.push(headers.join(",")); // header row

  rowsToExport.forEach((r) => {
  const amt  = Number(r.subtotal || 0);
  const cgst = Number(r.cgst || 0);
  const sgst = Number(r.sgst || 0);
  const igst = Number(r.igst || 0);
  const totalGst = cgst + sgst + igst;
  const grand = Number(r.grand_total || 0);

  // Make date text so Excel shows full year (e.g., 09-Aug-2025)
  const dateText = fmtDate(r.created_on);
  const csvDate  = `'${dateText}`; // apostrophe forces text in Excel

  const row = [
    csvCell(r.ti_no || ""),
    csvCell(csvDate),
    csvCell(r.to_company || ""),
    csvCell(r.to_gstin || ""),
    amt.toFixed(2),
    cgst.toFixed(2),
    sgst.toFixed(2),
    igst.toFixed(2),
    totalGst.toFixed(2),
    String(Math.round(grand)), // rounded total amount
  ];
  lines.push(row.join(","));
});


  const csv = "\uFEFF" + lines.join("\n"); // UTF-8 BOM
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const fname = `Tax_Invoices_${formatForFile(fromDate)}_to_${formatForFile(toDate)}.csv`;
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fname;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};



  return (
    <>
      <Header />
      <div style={{ paddingTop: "70px", paddingBottom: "70px" }}>
        <div className="container-fluid px-3 py-3">
          {/* Breadcrumb + Back */}
          <div className="d-flex align-items-center justify-content-between mb-4">
            <ul className="breadcrumb breadcrumb-transparent breadcrumb-dot font-weight-bold mb-0 p-0">
              <li className="breadcrumb-item">
                <Link to="/home" className="text-muted">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/pages/invoicing/Invoicing" className="text-muted">
                  Invoicing
                </Link>
              </li>
              <li className="breadcrumb-item">
                <span className="text-dark font-weight-bold">View Tax Invoices</span>
              </li>
            </ul>
          </div>

          {/* Card */}
          <div className="card card-custom shadow-sm">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4 border-bottom pb-2">
                  <h5 className="font-weight-bold mb-0">Tax Invoices List</h5>
                  <div>
                    <Link to="/pages/invoicing/Invoicing" className="btn btn-light" style={{ marginRight: "10px" }}>
                      <i className="fa fa-arrow-left"></i> Back
                    </Link>
                    <button type="button" onClick={exportToCSV} className="btn btn-primary">
                      <i className="fas fa-download" style={{ marginRight: "10px" }}></i>
                      Export
                    </button>
                  </div>
                </div>

              {/* Filters */}
              <div className="row mb-4">
                <div className="col-md-3">
                  <label className="font-weight-bold text-dark">From Date</label>
                  <input
                    type={fromDateType}
                    className="form-control"
                    value={
                      fromDateType === "date"
                        ? fromDate
                        : new Date(fromDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                    }
                    onFocus={() => setFromDateType("date")}
                    onBlur={() => setFromDateType("text")}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-md-3">
                  <label className="font-weight-bold text-dark">To Date</label>
                  <input
                    type={toDateType}
                    className="form-control"
                    value={
                      toDateType === "date"
                        ? toDate
                        : new Date(toDate).toLocaleDateString("en-GB", {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                          })
                    }
                    onFocus={() => setToDateType("date")}
                    onBlur={() => setToDateType("text")}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-md-6">
                  <label className="font-weight-bold text-dark">Search</label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search by TI No, Company, Brand or Type"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              <DataTable
                columns={columns}
                data={filteredRows}
                progressPending={loading}
                pagination
                highlightOnHover
                responsive
                striped
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ViewTax;
